import React, { useState } from 'react'
import { Plus, Trash2, Edit2 } from 'lucide-react'
import { WheelOption } from '../types'

interface OptionListProps {
  options: WheelOption[]
  onAddOption: (text: string) => void
  onRemoveOption: (id: string) => void
  onUpdateOption: (id: string, text: string) => void
}

export const OptionList: React.FC<OptionListProps> = ({
  options,
  onAddOption,
  onRemoveOption,
  onUpdateOption
}) => {
  const [newOption, setNewOption] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')

  const handleAddOption = () => {
    if (newOption.trim()) {
      onAddOption(newOption.trim())
      setNewOption('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddOption()
    }
  }

  const startEditing = (option: WheelOption) => {
    setEditingId(option.id)
    setEditingText(option.text)
  }

  const saveEdit = () => {
    if (editingId && editingText.trim()) {
      console.log('Saving option:', editingId, editingText.trim())
      onUpdateOption(editingId, editingText.trim())
      setEditingId(null)
      setEditingText('')
    }
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingText('')
  }

  return (
    <div className="card p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Decision Options</h3>
      
      {/* Add new option */}
      <div className="flex space-x-2 mb-4">
        <input
          type="text"
          value={newOption}
          onChange={(e) => setNewOption(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter option content..."
          className="input-field flex-1"
        />
        <button
          onClick={handleAddOption}
          disabled={!newOption.trim()}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Options list */}
      <div className="space-y-2">
        {options.map((option) => (
          <div
            key={option.id}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            {editingId === option.id ? (
              <div className="flex items-center space-x-2 flex-1">
                <input
                  type="text"
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                  onKeyDown={(e) => e.key === 'Escape' && cancelEdit()}
                  className="input-field flex-1 border-2 border-blue-300 focus:border-blue-500"
                  autoFocus
                />
                                  <button
                    onClick={saveEdit}
                    className="text-green-600 hover:text-green-700 font-medium px-3 py-1 rounded hover:bg-green-50 transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="text-gray-600 hover:text-gray-700 font-medium px-3 py-1 rounded hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
              </div>
            ) : (
              <>
                <span className="text-gray-900">{option.text}</span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => startEditing(option)}
                    className="text-gray-600 hover:text-gray-700 p-1"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onRemoveOption(option.id)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {options.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No options added yet</p>
          <p className="text-sm">Click the input field above to add options</p>
        </div>
      )}
    </div>
  )
} 